import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPreviewLink, getStatus, simulateVisit } from "../lib/safegeo-api";
import { countryLabel } from "../lib/format";
import { pickVisitorCountry, previewCountryHint } from "../lib/preview-country";
import { queryKeys } from "../lib/query-keys";
import { CountrySelect } from "./CountrySelect";
import { ErrorBanner, InfoBanner, WarningBanner } from "./PageBanner";

export type PreviewContext = "market" | "store" | "block" | "generic";

type Props = {
  prefer?: string[];
  /** What the result sentence should describe (R3). */
  context?: PreviewContext;
};

type ExplainOpts = {
  marketCountries?: string[];
  marketsEnabled?: boolean;
};

function explainDecision(
  code: string,
  kind: string,
  reasons: string[] | undefined,
  target: string | null | undefined,
  context: PreviewContext,
  opts: ExplainOpts = {},
): { tone: "info" | "warning"; text: string } {
  const label = countryLabel(code);
  const rs = reasons || [];
  const markets = (opts.marketCountries || [])
    .map((c) => String(c || "").toUpperCase().slice(0, 2))
    .filter(Boolean);
  const home = markets[0] || "";
  const inMarkets = markets.includes(code);
  const isPrimary = !!home && code === home;
  const askCountry =
    markets.find((c) => c !== home) || (home === "US" ? "CA" : "CA");

  if (context === "block") {
    if (kind === "block") {
      return {
        tone: "info",
        text: `A visitor from ${label} sees the blocked page.`,
      };
    }
    return {
      tone: "warning",
      text: `${label} isn’t blocked yet. Add that country under Block, save, then Preview again.`,
    };
  }

  if (kind === "block") {
    return {
      tone: "info",
      text: `A visitor from ${label} sees the blocked page.`,
    };
  }
  if (kind === "marketSuggest" || kind === "suggest") {
    return {
      tone: "info",
      text:
        context === "store"
          ? `A visitor from ${label} is asked to go to another store` +
            (target ? ` → ${target}` : "") +
            "."
          : `A visitor from ${label} is asked to switch currency` +
            (target ? ` → ${target}` : "") +
            ".",
    };
  }
  if (kind === "market" || kind === "redirect") {
    return {
      tone: "info",
      text:
        context === "market"
          ? `A visitor from ${label} switches currency automatically` +
            (target ? ` → ${target}` : "") +
            "."
          : `A visitor from ${label} is sent automatically` +
            (target ? ` → ${target}` : "") +
            ".",
    };
  }

  if (rs.includes("MARKET_NO_CHANGE")) {
    return {
      tone: "info",
      text: `Already on the right currency for ${label} - no ask.`,
    };
  }
  if (rs.includes("CART_NOT_EMPTY")) {
    return {
      tone: "info",
      text: `Cart had items - ${label} would be asked first instead of sent automatically.`,
    };
  }
  if (rs.includes("NO_MATCH") || kind === "none") {
    if (context === "market") {
      if (isPrimary) {
        return {
          tone: "info",
          text: `${label} is your primary market - shoppers are already home, so SafeGeo does nothing. Preview ${countryLabel(askCountry)} to see an ask.`,
        };
      }
      if (!opts.marketsEnabled) {
        return {
          tone: "warning",
          text: `Market redirect is off. Turn on Ask first (or Auto), save, then Preview ${countryLabel(askCountry)}.`,
        };
      }
      if (!inMarkets) {
        return {
          tone: "info",
          text: `${label} isn’t in your Shopify Markets, so SafeGeo stays quiet (no ask). That’s expected for Developed / high-population checks. Preview ${countryLabel(askCountry)} (a Market country) to see the ask.`,
        };
      }
      return {
        tone: "warning",
        text: `No ask for ${label} yet. Confirm Market redirect is on and ${label} is in Shopify Markets, then Preview again.`,
      };
    }
    if (context === "store") {
      return {
        tone: "warning",
        text: `Nothing happens for ${label} yet. Add a Store redirect rule for that country, then Preview.`,
      };
    }
    return {
      tone: "warning",
      text: `Nothing happens for ${label} yet. Use a country that isn’t your home market.`,
    };
  }

  return {
    tone: "info",
    text: `A visitor from ${label}: ${kind || "no action"}.`,
  };
}

function introFor(context: PreviewContext): string {
  if (context === "block") {
    return "Check here = text result. Open in your store = real blocked page. Counts under Home → Test Mode (not Live Mode).";
  }
  if (context === "store") {
    return "Check here = text result. Open in your store = real popup on the storefront. Counts under Home → Test Mode (not Live Mode).";
  }
  if (context === "market") {
    return "Check here = text result only. Open in your store = see the real currency ask on the storefront. Counts under Home → Test Mode (not Live Mode).";
  }
  return "Pick a country to see exactly what that visitor would get. Results count under Test Mode and don’t use plan redirects.";
}

export function PreviewAsVisitor({
  prefer = [],
  context = "generic",
}: Props) {
  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const [simCountry, setSimCountry] = useState(() =>
    pickVisitorCountry(prefer, "CA"),
  );
  const [simResult, setSimResult] = useState<string | null>(null);
  const [simWarn, setSimWarn] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openHint, setOpenHint] = useState<string | null>(null);
  const workerOk = statusQ.data?.worker_configured !== false;
  const nestPreviewOk = statusQ.data?.preview_verify_configured !== false;
  const canOpenStore = workerOk || nestPreviewOk;
  const marketsLive = !!statusQ.data?.settings?.markets_enabled;

  const countryHint = useMemo(
    () => previewCountryHint(prefer, simCountry),
    [prefer.join("|"), simCountry],
  );

  useEffect(() => {
    if (!prefer.length) return;
    setSimCountry((cur) => {
      const next = pickVisitorCountry(prefer, cur);
      return next || cur;
    });
  }, [prefer.join("|")]);

  async function run(cc?: string) {
    const code = (cc || simCountry).trim().toUpperCase().slice(0, 2);
    if (cc) setSimCountry(code);
    setSimError(null);
    setSimResult(null);
    setSimWarn(false);
    setOpenHint(null);
    setBusy(true);
    try {
      const result = await simulateVisit({ country: code });
      const kind = result.decision?.kind || "none";
      const explained = explainDecision(
        code,
        kind,
        result.decision?.reasons,
        result.decision?.target,
        context,
        {
          marketCountries: prefer,
          marketsEnabled: marketsLive,
        },
      );
      setSimWarn(explained.tone === "warning");
      setSimResult(explained.text);
    } catch (e) {
      setSimError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function openInStore() {
    const code = simCountry.trim().toUpperCase().slice(0, 2);
    setSimError(null);
    setOpenHint(null);
    if (!canOpenStore) {
      setSimError(
        "Preview verify isn’t configured (SAFEGEO_WORKER_URL or API_PUBLIC_URL). Publish after API deploy, then try again.",
      );
      return;
    }
    if (!marketsLive && prefer.length >= 2 && context === "market") {
      setOpenHint(
        "Market redirect is still off - save Ask first or Switch automatically on Market redirect and Publish before expecting a storefront change.",
      );
    }
    setBusy(true);
    try {
      const link = await createPreviewLink(code);
      window.open(link.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setSimError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <s-section
      heading="Preview as a visitor"
      id="preview"
      subheading={introFor(context)}
    >
      <CountrySelect
        label="Where is your shopper?"
        value={simCountry}
        onChange={(code) => {
          setSimCountry(code);
          setSimResult(null);
          setSimError(null);
          setOpenHint(null);
        }}
        prefer={prefer}
        details={countryHint}
      />
      {!canOpenStore ? (
        <s-box paddingBlockStart="base">
          <WarningBanner heading="Storefront preview unavailable">
            Need SAFEGEO_WORKER_URL or API_PUBLIC_URL on the API, then Publish.
            Admin Preview (below) still works from the server engine.
          </WarningBanner>
        </s-box>
      ) : null}
      <s-box paddingBlockStart="base">
        <s-stack direction="inline" gap="base" alignItems="center">
          <s-button
            variant="primary"
            disabled={busy || !canOpenStore}
            onClick={() => void openInStore()}
          >
            Open in your store
          </s-button>
          <s-button
            variant="tertiary"
            disabled={busy}
            loading={busy}
            onClick={() => void run()}
          >
            Check result here
          </s-button>
        </s-stack>
      </s-box>
      <s-box paddingBlockStart="small-100">
        <s-paragraph color="subdued">
          Open in your store loads the shop with a fake country (?geo_test).
          Check result here only runs the engine and shows text - no new tab.
        </s-paragraph>
      </s-box>
      {simError ? (
        <ErrorBanner heading="Preview failed">{simError}</ErrorBanner>
      ) : null}
      {openHint ? (
        <s-box paddingBlockStart="base">
          <WarningBanner heading="Storefront tip">{openHint}</WarningBanner>
        </s-box>
      ) : null}
      {simResult ? (
        <s-box paddingBlockStart="base">
          {simWarn ? (
            <WarningBanner heading={`Result for ${countryLabel(simCountry)}`}>
              {simResult}
            </WarningBanner>
          ) : (
            <InfoBanner heading={`Result for ${countryLabel(simCountry)}`}>
              {simResult}{" "}
              {canOpenStore
                ? "To see it on the storefront, use Open in your store."
                : null}
            </InfoBanner>
          )}
        </s-box>
      ) : null}
    </s-section>
  );
}
